import type { components } from 'types/api';

import { EXAM_QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';

export type QuestionType =
  typeof EXAM_QUESTION_TYPES[keyof typeof EXAM_QUESTION_TYPES];

// Updated to match backend raw status values
export enum TaskStatus {
  DRAFT = 'draft',
  OPEN = 'open',
  CLOSED = 'closed',
  ARCHIVED = 'archived',
}

export enum TaskLifecycleStage {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  ARCHIVED = 'ARCHIVED',
}
export enum AssignmentLifecycleStage {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CLOSED = 'CLOSED',
  ARCHIVED = 'ARCHIVED',
}

export enum TaskLifecycleStageAction {
  CLOSE = 'close',
  ARCHIVE = 'archive',
  UNARCHIVE = 'unarchive',
}
export enum AssignmentLifecycleStageAction {
  ARCHIVE = 'archive',
  UNARCHIVE = 'unarchive',
}
export const actionToTaskFieldMap: Record<TaskLifecycleStageAction, string> = {
  [TaskLifecycleStageAction.CLOSE]: 'resolved_stage',
  [TaskLifecycleStageAction.ARCHIVE]: 'archived_at',
  [TaskLifecycleStageAction.UNARCHIVE]: 'archived_at',
};

export enum TaskAssignmentStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export const TaskAssignmentStatusText = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
};

export enum ResponseStatus {
  SKIPPED = 'SKIPPED',
  COMPLETED = 'COMPLETED',
}

export enum ContentType {
  EXAM_QUESTION = 'exams.examquestion',
  VIDEO_CHUNK = 'tod.chunk',
}

type WistiaVideo = components['schemas']['WistiaVideo'];

export interface SolvedExamQuestionAnswer {
  id: number;
  is_correct: boolean;
  feedback: string;
  sort_order: number;
  serial: string;
  text: string;
}
export interface Solution {
  explanation: string;
  video?: WistiaVideo | null;
  exemplar_response: string;
  exemplar_response_checklist_items: unknown[];
  annotation_image_url: string | null;
  checklist_image_annotations: unknown;
}

export interface SolvedExamQuestion {
  id: QuestionId;
  related_lessons: unknown[];
  knowledge_units: unknown[];
  answers: SolvedExamQuestionAnswer[] | [];
  solution?: Solution;
}

export interface ChunkContentObject {
  id: number;
  title: string;
  chunk_type:
    | 'activity'
    | 'interactive_activity'
    | 'pdf'
    | 'question'
    | 'success_measure'
    | 'video'
    | 'youtube';
  chunk_context: 'question_response' | null;
  video: WistiaVideo;
  lesson_id: number;
  lesson_title: string;
  duration: number;
}

export type ContentObject = Question | ChunkContentObject;

type SpicinessLevel = 1 | 2 | 3;

export interface Question {
  id: QuestionId;
  parent: QuestionId | null;
  instructions: string | string[];
  instructions_text: string;
  question_type: QuestionType;
  marks: string; // number as string
  marks_text: string; // e.g. '1 mark', '0 marks', '2 marks', '3.5 marks'
  level: number;
  image_url: string | null;
  question_level: unknown | null;
  vcaa_average: unknown | null;
  sort_order: number;
  number_of_lines: number;
  resource: unknown | null;
  spiciness_level: SpicinessLevel | null;
  knowledge_units: unknown[];
  skill: unknown | null;
  duration: string; // display value: '00:03:00';
  duration_in_seconds: number;
  serial: string;
  full_serial: string;
  answers: unknown[];
  working_pathways: SpicinessLevel[];
  fields: unknown[];
  calculators_allowed: boolean;
  source: string;
  difficulty: number;
  tree_id: number;
  options: unknown[];
  children: unknown[];
  solution?: Solution;
  full_instructions_list: string[];
  full_serial_list: string[];
}

export interface ContentSummary {
  content_type: ContentType;
  count: number;
  count_done: number;
  duration_in_seconds: string;
}
export interface Task {
  id: TaskId;
  title: string;
  start_datetime: DateISO8601 | string | null | Date;
  due_datetime: DateISO8601 | string | null | Date;
  creator: ProfileId;
  task_items: TaskItem[];
  task_assignments: TaskAssignment[];
  course_ids: string[];
  type: TaskType;
  status: TaskStatus;
  soft_deleted: boolean;
  closed_at_end_datetime: DateISO8601 | string | null | Date;
  archived_at: DateISO8601 | string | null | Date;
  resolved_stage: TaskLifecycleStage;
  enforce_due_datetime: boolean;
  num_assignments_completed: number;
  num_assignments_total: number;
  task_items_count: number;
  total_duration: number;
}

export interface TaskAssignment {
  task: TaskId;
  profile: ProfileId;
  // The fields below are nullable because they're not required when
  // creating a new task assignment (ie: because they're assigned by
  // the endpoint/database). In all other cases (eg: when retrieving an
  // existing task assignment) they will be populated.
  // TODO: separate creation, retrieval and update types.
  id: number | null;
  started_at: Date | string | DateISO8601 | null;
  submitted_at: Date | string | DateISO8601 | null;
  completion_status: TaskAssignmentStatus;
  resolved_stage: AssignmentLifecycleStage;
  num_responses: number;
}

type ContentTypeId = QuestionId; // or ActivityId | LessonId etc

interface ContentItemMetadata {
  exam_ids?: string[];
  topic_ids?: string[];
  course_ids?: string[];
  lesson_ids?: string[];
  question_types?: QuestionType[];
  chunk_types?: string[];
  chunk_contexts?: string[];
}

type ContentItemId = number;

export interface TaskItem {
  id: number;
  task: TaskId;
  sort_order: number;
  content_item: ContentItem;
}
interface ContentItemBase {
  id: ContentItemId;
  metadata: ContentItemMetadata;
  object_id: ContentTypeId;
}

export interface QuestionContentItem extends ContentItemBase {
  content_type: ContentType.EXAM_QUESTION;
  content_object: Question;
}

export interface VideoChunkContentItem extends ContentItemBase {
  content_type: ContentType.VIDEO_CHUNK;
  content_object: ChunkContentObject;
}

export type ContentItem = QuestionContentItem | VideoChunkContentItem;

export function isQuestionContentItem(
  item: ContentItem
): item is QuestionContentItem {
  return item.content_type === ContentType.EXAM_QUESTION;
}

export function isVideoChunkContentItem(
  item: ContentItem
): item is VideoChunkContentItem {
  return item.content_type === ContentType.VIDEO_CHUNK;
}

export type ExamResponse = components['schemas']['PolymorphicExamResponse'];

export interface TaskItemResponse {
  id: number;
  profile: number;
  task_assignment: number;
  task_item: number;
  response_object: number;
  student_response: ExamResponse | null;
  created_at: string;
  updated_at: string;
  is_correct: boolean;
  is_grokked: boolean | null;
  is_complete: boolean;
}

export enum TaskType {
  Created = 'created',
  SpacedRetrieval = 'spaced_retrieval',
}
